import { AbstractService, ApiResponse, PaginatedResponse } from './core/AbstractService';
import { Admin } from './core/types';
import api from './core/api';

interface AdminUserStats {
  total: number;
  active: number;
  superAdmins: number;
  admins: number;
  moderators: number;
}

interface CreateAdminData {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MODERATOR';
}

interface UpdateAdminData {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: 'SUPER_ADMIN' | 'ADMIN' | 'MODERATOR';
  isActive?: boolean;
}

class AdminUserService extends AbstractService<Admin> {
  constructor() {
    super('/admin');
  }

  /**
   * Get all admin users, server-paginated/filtered via GET /api/admin.
   */
  async getAll(params?: URLSearchParams): Promise<PaginatedResponse<Admin>> {
    const query = params ? `?${params.toString()}` : '';
    const response = await api.get<ApiResponse<{ admins: Admin[]; total: number; page: number; limit: number; totalPages: number }>>(
      `${this.basePath}${query}`
    );
    const { admins, ...rest } = response.data.data;

    return {
      success: response.data.success,
      data: { items: admins, ...rest },
    };
  }

  /**
   * Get admin user statistics
   */
  async getStats(): Promise<ApiResponse<AdminUserStats>> {
    const response = await api.get<ApiResponse<AdminUserStats>>(`${this.basePath}/stats`);
    return response.data;
  }

  /**
   * Create a new admin user
   */
  async create(data: CreateAdminData): Promise<ApiResponse<Admin>> {
    const response = await api.post<ApiResponse<Admin>>(
      this.basePath,
      data
    );
    return response.data;
  }

  /**
   * Update an admin user
   */
  async update(id: string, data: UpdateAdminData): Promise<ApiResponse<Admin>> {
    const response = await api.put<ApiResponse<Admin>>(
      `${this.basePath}/${id}`,
      data
    );
    return response.data;
  }

  /**
   * Change admin user password
   */
  async changePassword(id: string, password: string): Promise<ApiResponse<void>> {
    const response = await api.put<ApiResponse<void>>(
      `${this.basePath}/${id}/password`,
      { newPassword: password }
    );
    return response.data;
  }

  /**
   * Delete an admin user
   */
  async remove(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(
      `${this.basePath}/${id}`
    );
    return response.data;
  }
}

export const adminUserService = new AdminUserService();
export default adminUserService;
